import React, { useState, useEffect, useRef } from 'react';
import { Search, Target, X, BookOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { api as axios } from '../../api/api';

export function RoleCombobox({ value, onChange, placeholder = "Search Job Title or Role..." }) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const [popoverId] = useState(() => `role-combobox-${Math.random().toString(36).substring(2, 11)}`);

    // Debounce search
    useEffect(() => {
        if (!open) return;
        
        const timeoutId = setTimeout(() => {
            if (inputValue.length >= 2) {
                fetchSuggestions(inputValue);
            } else {
                setSuggestions([]);
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [inputValue, open]);

    const fetchSuggestions = async (query) => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/global-pool/roles/suggest?q=${encodeURIComponent(query)}`);
            setSuggestions(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch role suggestions', err);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (suggestion) => {
        onChange(suggestion.id); // e.g., 'SALES_LEADERSHIP' or 'exact:vp sales'
        setInputValue('');
        setOpen(false);
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        onChange('');
        setInputValue('');
    };

    // Helper to format the display of the current active value
    // E.g. If the parent passes down 'SALES_LEADERSHIP', we want to display it correctly
    // Since the component is semi-controlled, we'll try to resolve it visually.
    const displayValue = () => {
        if (!value) return '';
        if (value.startsWith('exact:')) return value.split('exact:')[1];
        // If it's a family ID, just format it nicely
        return value.replace(/_/g, ' ');
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={open ? popoverId : undefined}
                    aria-haspopup="listbox"
                    className="flex items-center w-full h-9 bg-muted/30 border border-border rounded-lg px-3 text-xs focus-within:border-primary transition-colors cursor-text relative"
                    onClick={() => inputRef.current?.focus()}
                >
                    <Search className="w-3.5 h-3.5 mr-2 text-muted-foreground flex-shrink-0" />
                    
                    {value ? (
                        <div className="flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 rounded px-1.5 py-0.5 font-bold uppercase tracking-wider text-[10px] w-full mr-6 truncate">
                            {displayValue()}
                        </div>
                    ) : (
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            className="bg-transparent border-none outline-none w-full text-foreground placeholder:text-muted-foreground"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onFocus={() => { setOpen(true); if (inputValue.length >= 2) fetchSuggestions(inputValue); }}
                        />
                    )}

                    {value && (
                        <X 
                            className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground absolute right-3 cursor-pointer" 
                            onClick={clearSelection}
                        />
                    )}
                </div>
            </PopoverTrigger>
            
            <PopoverContent id={popoverId} className="w-[300px] p-0 shadow-2xl rounded-xl overflow-hidden border-border/50" align="start" sideOffset={8}>
                <div className="flex flex-col max-h-[300px] overflow-y-auto custom-scrollbar">
                    {loading && (
                        <div className="p-4 text-xs font-bold text-muted-foreground text-center animate-pulse">
                            Generating smart matches...
                        </div>
                    )}
                    
                    {!loading && suggestions.length === 0 && inputValue.length >= 2 && (
                        <div className="p-6 flex flex-col items-center justify-center text-center space-y-2">
                            <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
                                <Search className="w-4 h-4 text-muted-foreground opacity-50" />
                            </div>
                            <div className="text-xs font-black text-foreground">No intelligent matches</div>
                            <div className="text-[10px] text-muted-foreground font-medium">
                                We'll still search for exact occurrences of "{inputValue}".
                            </div>
                            <button 
                                onClick={() => handleSelect({ id: `exact:${inputValue}`, label: inputValue })}
                                className="mt-2 text-[10px] font-bold text-primary uppercase tracking-widest hover:underline"
                            >
                                Search Exact Title
                            </button>
                        </div>
                    )}

                    {suggestions.map((s, i) => (
                        <div
                            key={i}
                            onClick={() => handleSelect(s)}
                            className="p-3 border-b border-border/30 hover:bg-muted/30 cursor-pointer transition-colors flex items-start gap-3 group"
                        >
                            <div className={`mt-0.5 p-1.5 rounded-md flex-shrink-0 ${s.type === 'FAMILY' ? 'bg-indigo-500/10 text-indigo-500 group-hover:bg-indigo-500/20' : 'bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20'}`}>
                                {s.type === 'FAMILY' ? <BookOpen className="w-3.5 h-3.5" /> : <Target className="w-3.5 h-3.5" />}
                            </div>
                            <div className="flex-1 overflow-hidden">
                                <div className="text-sm font-bold text-foreground flex items-center justify-between">
                                    {s.label}
                                    {s.type === 'FAMILY' && (
                                        <span className="text-[9px] uppercase tracking-widest font-black bg-indigo-500/10 text-indigo-500 px-1.5 rounded-sm">Role Family</span>
                                    )}
                                </div>
                                <div className="text-[10px] text-muted-foreground font-medium truncate mt-0.5">
                                    {s.description}
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {!loading && suggestions.length === 0 && inputValue.length < 2 && (
                        <div className="p-4 text-xs font-medium text-muted-foreground text-center">
                            Type at least 2 characters to see intelligent matches...
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default RoleCombobox;
